import { Link } from "@tanstack/react-router";
import { CalendarCheck } from "lucide-react";
import type { ReactNode } from "react";

import { ClockRing } from "@/features/site/components/clock-ring";
import { DotRing } from "@/features/site/components/dot-ring";
import { card, ghostButton, inkTag, primaryCard, smallButton, tag } from "@/features/site/components/section";
import { SiteBrand } from "@/features/site/components/site-head";
import { siteVars } from "@/features/site/components/site-theme";
import { usePublicDirectory } from "@/features/site/hooks/use-public-directory";
import { pickFromRedirect } from "@/features/site/utils/booking-link";
import { soonestOpening } from "@/features/site/utils/directory";
import { openTimeLabelIn } from "@/features/site/utils/labels";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { appConfig } from "@complete-athlete/enums";

/**
 * The starter's forms, dressed in the site's clothes through deep selectors: the shell owns the
 * headline (so the forms' own h1 goes), fields become recessed wells, the submit becomes the
 * site's primary pill and everything secondary becomes a quiet well pill. The forms themselves
 * stay the starter's, so either starter (and any update to it) keeps working underneath.
 */
/** Days of open times asked for: the soonest one is all this page shows. */
const CALENDAR_DAYS = 5;

const formSurface = cn(
  "mt-8 w-full text-left",
  "[&_h1]:hidden [&_[data-slot=field-group]>div:first-child:has(>h1:first-child)]:hidden",
  "[&_form]:gap-5 [&_[data-slot=field-group]]:gap-5 [&_[data-slot=field]]:gap-2",
  "[&_[data-slot=field-label]]:text-[13px] [&_[data-slot=field-label]]:font-medium [&_[data-slot=field-label]]:text-foreground",
  "[&_input:not([type=checkbox])]:h-12 [&_input:not([type=checkbox])]:rounded-2xl [&_input:not([type=checkbox])]:border-transparent [&_input:not([type=checkbox])]:bg-[var(--site-well)] [&_input:not([type=checkbox])]:px-4 [&_input:not([type=checkbox])]:text-[15px] [&_input:not([type=checkbox])]:shadow-none [&_input:not([type=checkbox])]:transition-[background-color,box-shadow]",
  "[&_input:not([type=checkbox]):focus-visible]:border-transparent [&_input:not([type=checkbox]):focus-visible]:bg-[var(--site-well)] [&_input:not([type=checkbox]):focus-visible]:ring-0 [&_input:not([type=checkbox]):focus-visible]:shadow-[0_0_0_2px_var(--site-ink)]",
  "[&_button[data-slot=button]]:h-12 [&_button[data-slot=button]]:rounded-full [&_button[data-slot=button]]:text-sm [&_button[data-slot=button]]:font-semibold [&_button[data-slot=button]]:shadow-none [&_button[data-slot=button]]:transition-transform active:[&_button[data-slot=button]]:scale-[0.98]",
  // Primary actions (submit, "Open dashboard") are the site's primary pill.
  "[&_button[type=submit]]:!border-transparent [&_button[type=submit]]:!bg-primary [&_button[type=submit]]:![background-image:none] [&_button[type=submit]]:!text-primary-foreground",
  "[&_button[data-slot=button]:not([type=submit]):not(.border):not(:has(.lucide-log-out))]:!bg-primary [&_button[data-slot=button]:not([type=submit]):not(.border):not(:has(.lucide-log-out))]:![background-image:none] [&_button[data-slot=button]:not([type=submit]):not(.border):not(:has(.lucide-log-out))]:!text-primary-foreground",
  // Secondary actions (Google, sign out) are the quiet well pill.
  "[&_button[data-slot=button].border]:!border-transparent [&_button[data-slot=button].border]:!bg-[var(--site-well)] [&_button[data-slot=button].border]:!text-foreground hover:[&_button[data-slot=button].border]:!bg-muted",
  "[&_button:has(.lucide-log-out)]:!bg-transparent [&_button:has(.lucide-log-out)]:!text-muted-foreground hover:[&_button:has(.lucide-log-out)]:!text-foreground",
  "[&_[data-slot=field-separator]]:text-xs [&_[data-slot=field-separator]]:text-muted-foreground",
  "[&_[data-slot=field-description]]:text-[13px] [&_[data-slot=field-description]]:text-muted-foreground",
  "[&_a]:font-medium [&_a]:text-foreground [&_a]:underline-offset-4 hover:[&_a]:underline",
);

export type AuthShellProps = {
  /** The small tag above the title, e.g. "Sign in". */
  eyebrow: string;
  /** The headline, two words in all: the lead, then a quieter word after it. */
  title: { lead: string; accent: string };
  description: string;
  /** The way over to the other auth page, e.g. New here? -> Create an account. */
  aside: { prompt: string; label: string; to: string };
  /** The post-login redirect, when the page was opened with one. */
  redirect?: string;
  children: ReactNode;
};

/**
 * The auth pages in the site's own look, so signing in reads as the next step of booking and
 * not as a different product. Left: the form card. Right: the solid card with one time on a
 * ring. A visitor who came from the open calendar sees the time they picked, so they know it
 * survived the detour; anyone else sees the studio's next open time.
 */
export function AuthShell({ eyebrow, title, description, aside, redirect, children }: AuthShellProps) {
  const { directory } = usePublicDirectory({ days: CALENDAR_DAYS });
  const pick = pickFromRedirect(redirect);
  const picked = pick ? directory?.specialists.find((specialist) => specialist.id === pick.specialistId) : undefined;
  const pickZone = picked?.timeZone ?? directory?.business.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const next = pick || !directory ? null : soonestOpening(directory.specialists);
  // What the ring shows: the visitor's own pick first, the studio's next open time otherwise.
  const shown = pick
    ? { startAt: pick.startAt, timeZone: pickZone }
    : next
      ? { startAt: next.startAt, timeZone: next.specialist.timeZone }
      : null;

  return (
    <div
      style={siteVars}
      className="grid min-h-svh gap-3 bg-page-background p-3 text-sm text-foreground lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]"
    >
      <div className={cn(card, "flex flex-col md:px-9")}>
        <header className="flex items-center justify-between gap-4">
          <SiteBrand />
          <div className="flex items-center gap-3">
            <span className="text-[13px] text-muted-foreground max-sm:hidden">{aside.prompt}</span>
            <Link to={aside.to} search={redirect ? { redirect } : undefined} className={cn(ghostButton, smallButton)}>
              {aside.label}
            </Link>
          </div>
        </header>

        <main className="mx-auto flex w-full max-w-[420px] flex-1 flex-col justify-center py-12">
          <span className={tag}>{eyebrow}</span>
          <h1 className="mt-5 text-[clamp(2.5rem,4.2vw,3.75rem)] leading-[0.98] font-medium tracking-[-0.045em]">
            {title.lead} <span className="text-muted-foreground">{title.accent}</span>
          </h1>
          <p className="mt-4 max-w-[38ch] text-base text-muted-foreground">{description}</p>

          {/* Phones have no side card, so the pick is repeated here. */}
          {pick ? (
            <div className="mt-6 flex items-center gap-3 rounded-[20px] bg-primary p-3 pr-4 text-primary-foreground lg:hidden">
              <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary-foreground/10">
                <CalendarCheck aria-hidden className="size-[18px]" />
              </span>
              <span className="text-sm">
                <b className="block font-semibold">
                  {m.auth_shell_pick_title({ time: openTimeLabelIn(pick.startAt, pickZone) })}
                </b>
                {picked ? m.auth_shell_pick_with({ name: picked.name }) : m.auth_shell_pick_waiting()}
              </span>
            </div>
          ) : null}

          <div className={formSurface}>{children}</div>
        </main>

        <footer className="flex flex-wrap items-center justify-end gap-3 text-[13px] text-muted-foreground">
          <Link to="/" className="transition-colors hover:text-foreground">
            {m.auth_shell_back()}
          </Link>
        </footer>
      </div>

      <aside
        className={cn(primaryCard, "hidden flex-col items-center justify-between gap-8 text-center md:p-11 lg:flex")}
      >
        <span className={cn(inkTag, "self-center")}>{pick ? m.auth_shell_pick_tag() : m.services_fact_next()}</span>
        {shown ? (
          <ClockRing
            startAt={shown.startAt}
            timeZone={shown.timeZone}
            tone="primary"
            size="text-[clamp(3.5rem,6vw,5.5rem)]"
            className="aspect-square w-[min(380px,82%)]"
          />
        ) : (
          <DotRing filled={0} total={32} dot={2.6} tone="primary" className="aspect-square w-[min(380px,82%)]">
            <span className="block max-w-[6em] text-[clamp(2rem,3.4vw,3rem)] leading-[0.95] font-bold tracking-[-0.04em] text-balance">
              {appConfig.name}
            </span>
          </DotRing>
        )}
        <div>
          <p className="text-[22px] font-semibold tracking-[-0.02em]">
            {pick ? (picked?.name ?? appConfig.name) : next ? next.specialist.name : (directory?.business.name ?? "")}
          </p>
          <p className="text-[var(--site-on-primary-soft)]">
            {pick ? m.auth_shell_pick_waiting() : next ? next.specialist.title : ""}
          </p>
        </div>
      </aside>
    </div>
  );
}
